import { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { contentEngineApi } from '../../services/contentEngineApi';

// Native, in-app reader for a Gutenberg chapter (plain text served by our backend).
export function GutenbergReader({ gutenbergId, chapter }) {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [attempt, setAttempt] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!chapter) return undefined;
    let cancelled = false;
    setState({ loading: true, error: '', data: null });
    contentEngineApi.readerGutenbergChapter(gutenbergId, chapter)
      .then((data) => { if (!cancelled) { setState({ loading: false, error: '', data }); scrollRef.current?.scrollTo(0, 0); } })
      .catch((e) => !cancelled && setState({ loading: false, error: e.message || 'Could not load chapter', data: null }));
    return () => { cancelled = true; };
  }, [gutenbergId, chapter, attempt]);

  if (state.loading) {
    return <div className="h-full grid place-items-center text-sm text-muted-foreground" data-testid="gutenberg-reader-loading"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  if (state.error) {
    return (
      <div className="h-full grid place-items-center p-6 text-center" data-testid="gutenberg-reader-error">
        <div className="space-y-3">
          <AlertCircle className="w-6 h-6 mx-auto text-amber-500" />
          <p className="text-sm text-muted-foreground">{state.error}</p>
          <Button size="sm" variant="outline" onClick={() => setAttempt((a) => a + 1)} data-testid="gutenberg-reader-retry"><RotateCcw className="w-3 h-3 mr-1" /> Retry</Button>
        </div>
      </div>
    );
  }
  return (
    <div ref={scrollRef} className="h-full overflow-y-auto bg-[#fbfaf7] dark:bg-slate-950" data-testid="gutenberg-reader">
      <article className="max-w-2xl mx-auto px-6 sm:px-10 py-10 font-serif text-[17px] leading-8 text-slate-800 dark:text-slate-200">
        <h2 className="text-xl font-bold mb-6 font-heading" data-testid="gutenberg-chapter-title">{state.data.title}</h2>
        {state.data.text.split(/\n\s*\n/).map((p, i) => (
          <p key={i} className="mb-5 whitespace-pre-line">{p.replace(/\n/g, ' ')}</p>
        ))}
      </article>
    </div>
  );
}
